import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isChunkLoadError } from '@/lib/chunk-recovery';
import { hardReload } from '@/lib/hard-reload';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * 최상위 ErrorBoundary — 최후 방어선(전체 화면 폴백).
 * - 청크 로드 실패(배포 후 옛 청크)면 원인 안내 + '다시 불러오기'(hardReload) 하나만 — 자동 복구는 lib/chunk-recovery.ts
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError && isChunkLoadError(this.state.error)) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <h1 className="mb-2 text-xl font-semibold text-gray-900 dark:text-gray-100">
              새 버전이 배포되어 화면을 다시 불러와야 합니다
            </h1>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              열려던 화면이 이전 버전 파일을 찾고 있습니다. 아래 버튼을 누르면 새 버전으로 다시 불러옵니다.
            </p>
            <button
              type="button"
              onClick={() => void hardReload()}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              다시 불러오기
            </button>
          </div>
        </div>
      );
    }

    if (this.state.hasError) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
          <div className="text-center">
            <h1 className="mb-2 text-xl font-semibold text-gray-900 dark:text-gray-100">
              오류가 발생했습니다
            </h1>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              예기치 않은 오류가 발생했습니다. 페이지를 새로고침해 주세요.
            </p>
            <button
              type="button"
              onClick={this.handleReload}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              새로고침
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
